import {
  appointableListQueryFn,
  useMutationRuleGroupEdit,
  useMutationRuleGroupNew,
} from "@gymflow/api";
import { FormikInput, onlyNumbersProps } from "@gymflow/common";
import { cn } from "@gymflow/helpers";
import { RuleGroup, RuleType } from "@gymflow/types";
import { useFormik } from "formik";
import range from "lodash/range";
import { forwardRef, useImperativeHandle, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { useHistory, useParams } from "react-router";
import { toFormikValidationSchema } from "zod-formik-adapter";

import useGymflowModels from "../../../store";
import { PaginatedSelect } from "../../atoms";
import {
  AppointmentRuleFormType,
  AppointmentRuleMapper,
} from "./AppointmentRuleMapper";
import { RuleFormWrapper } from "./RuleFormWrapper";
import { RuleMapper } from "./RuleMapper";
import { RulesComponents } from "./RulesComponents";
import { YesNoRadioButtons } from "./YesNoRadioButtons";

export function AppointmentRuleForm() {
  const { t } = useTranslation();
  const { ruleGroupId } = useParams<{ ruleGroupId: string }>();
  const isEditing = ruleGroupId !== undefined && ruleGroupId !== "";
  return (
    <RuleFormWrapper
      title={
        isEditing
          ? t("page.rules.appointmentForm.headerEdit")
          : t("page.rules.appointmentForm.headerNew")
      }
      description={
        isEditing
          ? t("page.rules.appointmentForm.explanationEdit")
          : t("page.rules.appointmentForm.explanationNew")
      }
      Form={Form}
    />
  );
}

const Form = forwardRef<
  { submitForm: () => Promise<any> },
  | {
      isEditing: true;
      ruleTypeToIdMap: Record<RuleType, number>;
      initialValues: RuleGroup;
    }
  | {
      isEditing: false;
    }
>(function Form(props, ref) {
  const initialValues = useMemo(() => {
    return "initialValues" in props
      ? AppointmentRuleMapper.mapBEtoFE(props.initialValues)
      : AppointmentRuleMapper.defaultValues;
  }, [props]);
  const { ruleGroupId } = useParams<{ ruleGroupId: string }>();
  const history = useHistory();
  const { t } = useTranslation();
  const { api } = useGymflowModels();
  const { mutateAsync: newGroup } = useMutationRuleGroupNew({ api });
  const { mutateAsync: editGroup } = useMutationRuleGroupEdit({ api });

  const formik = useFormik<AppointmentRuleFormType>({
    initialValues,
    validateOnBlur: true,
    enableReinitialize: true,
    validationSchema: toFormikValidationSchema(AppointmentRuleMapper.schema),
    onSubmit: async (values) => {
      if (props.isEditing) {
        await editGroup({
          ruleGroupId: +ruleGroupId,
          patchedFields: RuleMapper.mapPostToPatch(
            AppointmentRuleMapper.mapFEtoBE(values),
            props.ruleTypeToIdMap,
          ),
        });
      } else {
        await newGroup({
          fields: AppointmentRuleMapper.mapFEtoBE(values),
        });
      }
      history.goBack();
    },
  });
  const { values, setFieldValue, submitForm } = formik;
  useImperativeHandle(ref, () => ({
    submitForm,
  }));
  return (
    <>
      {!values.isDefault && (
        <RulesComponents.FormRow
          label={t("page.rules.commonFields.ruleNameLabel")}
        >
          <FormikInput
            name="name"
            placeholder={t("page.rules.commonFields.ruleNamePlaceholder")}
            formikProps={formik}
          />
        </RulesComponents.FormRow>
      )}
      <RulesComponents.FormRow
        label={t("page.rules.appointmentForm.advancedBookingsLabel")}
        isDisabled={!values.advancedBookings.enabled}
        onChange={(e) => {
          const value: typeof values.advancedBookings = e
            ? {
                enabled: true,
                amount: 1,
                type: "HOURS",
              }
            : {
                enabled: false,
              };
          setFieldValue("advancedBookings", value);
        }}
      >
        {values.advancedBookings.enabled && (
          <RulesComponents.WindowPicker
            label={t("page.rules.appointmentForm.advancedBookingsExplanation")}
            onChange={(newValue) => {
              setFieldValue("advancedBookings", { enabled: true, ...newValue });
            }}
            value={values.advancedBookings}
            availableUnits={["HOURS", "DAYS", "MONTHS"]}
          />
        )}
      </RulesComponents.FormRow>
      <RulesComponents.FormRow
        label={t("page.rules.appointmentForm.bookingNoticeLabel")}
        isDisabled={!values.bookingNoticePeriod.enabled}
        onChange={(e) => {
          const value: typeof values.bookingNoticePeriod = e
            ? {
                enabled: true,
                amount: 1,
                type: "HOURS",
              }
            : {
                enabled: false,
              };
          setFieldValue("bookingNoticePeriod", value);
        }}
      >
        {values.bookingNoticePeriod.enabled && (
          <RulesComponents.WindowPicker
            label={t("page.rules.appointmentForm.bookingNoticeExplanation")}
            onChange={(newValue) => {
              setFieldValue("bookingNoticePeriod", {
                enabled: true,
                ...newValue,
              });
            }}
            value={values.bookingNoticePeriod}
            availableUnits={["MINUTES", "HOURS", "DAYS"]}
          />
        )}
      </RulesComponents.FormRow>
      <RulesComponents.FormRow
        label={t("page.rules.appointmentForm.rescheduleNoticeLabel")}
        tooltip={t("page.rules.appointmentForm.rescheduleNoticeTooltip")}
        isDisabled={!values.rescheduleNoticePeriod.enabled}
        onChange={(e) => {
          const value: typeof values.rescheduleNoticePeriod = e
            ? {
                enabled: true,
                amount: 1,
                type: "HOURS",
              }
            : {
                enabled: false,
              };
          setFieldValue("rescheduleNoticePeriod", value);
        }}
      >
        {values.rescheduleNoticePeriod.enabled && (
          <RulesComponents.WindowPicker
            label={t("page.rules.appointmentForm.rescheduleNoticeExplanation")}
            onChange={(newValue) => {
              setFieldValue("rescheduleNoticePeriod", {
                enabled: true,
                ...newValue,
              });
            }}
            value={values.rescheduleNoticePeriod}
            availableUnits={["MINUTES", "HOURS", "DAYS"]}
          />
        )}
      </RulesComponents.FormRow>

      <RulesComponents.FormRow
        label={t("page.rules.appointmentForm.concurrentBookingsLabel")}
      >
        <RulesComponents.CardLabel
          label={t("page.rules.appointmentForm.concurrentBookingsExplanation")}
        >
          <YesNoRadioButtons
            value={!values.concurrentBookings}
            onChange={(newValue) => {
              setFieldValue("concurrentBookings", !newValue);
            }}
          />
        </RulesComponents.CardLabel>
      </RulesComponents.FormRow>

      <RulesComponents.FormRow
        label={t("page.rules.appointmentForm.bookingLimitsLabel")}
        isDisabled={!values.bookingLimits.enabled}
        onChange={(e) => {
          const value: typeof values.bookingLimits = e
            ? {
                enabled: true,
                amount: 1,
              }
            : {
                enabled: false,
              };
          setFieldValue("bookingLimits", value);
        }}
      >
        <RulesComponents.CardLabel
          label={t("page.rules.appointmentForm.bookingLimitsExplanation")}
        >
          {values.bookingLimits.enabled && (
            <PaginatedSelect
              value={{ label: values.bookingLimits.amount }}
              onChange={(newValue) => {
                setFieldValue("bookingLimits.amount", newValue.value);
              }}
              loadOptions={async () => {
                return {
                  options: range(1, 15).map((e) => ({
                    label: `${e}`,
                    value: e,
                  })),
                };
              }}
            />
          )}
        </RulesComponents.CardLabel>
      </RulesComponents.FormRow>

      <RulesComponents.FormRow
        label={t("page.rules.appointmentForm.cancellationsLabel")}
        tooltip={t("page.rules.appointmentForm.cancellationsTooltip")}
        isDisabled={!values.cancelation.enabled}
        onChange={(e) => {
          const value: typeof values.cancelation = e
            ? {
                enabled: true,
                amount: 1,
                type: "HOURS",
                cancellationFee: {
                  enabled: false,
                },
              }
            : {
                enabled: false,
              };
          setFieldValue("cancelation", value);
        }}
      >
        {values.cancelation?.enabled && (
          <div className="flex w-full flex-col gap-2">
            <RulesComponents.WindowPicker
              label={t("page.rules.appointmentForm.cancellationsExplanation")}
              onChange={(newValue) => {
                setFieldValue("cancelation", {
                  ...values.cancelation,
                  enabled: true,
                  ...newValue,
                });
              }}
              value={values.cancelation}
              availableUnits={["HOURS", "DAYS", "MONTHS"]}
            />

            <RulesComponents.CardLabel
              label={t(
                "page.rules.appointmentForm.cancellationsFeeExplanation",
              )}
            >
              <YesNoRadioButtons
                value={values.cancelation.cancellationFee?.enabled}
                onChange={(newValue) => {
                  const value: typeof values.cancelation.cancellationFee =
                    newValue
                      ? {
                          enabled: true,
                          amount: 1,
                        }
                      : {
                          enabled: false,
                        };
                  setFieldValue("cancelation.cancellationFee", value);
                }}
              />
              <FormikInput
                className={cn("transition-opacity w-16", {
                  "opacity-0": !values.cancelation.cancellationFee?.enabled,
                })}
                name="cancelation.cancellationFee.amount"
                formikProps={formik}
                {...onlyNumbersProps}
              />
            </RulesComponents.CardLabel>
          </div>
        )}
      </RulesComponents.FormRow>

      <RulesComponents.FormRow
        label={t("page.rules.appointmentForm.noShowsLabel")}
        tooltip={t("page.rules.appointmentForm.noShowsTooltip")}
      >
        <RulesComponents.CardLabel
          label={t("page.rules.appointmentForm.noShowsExplanation")}
        >
          <YesNoRadioButtons
            value={values.noShowFee.enabled}
            onChange={(newValue) => {
              const value: typeof values.noShowFee = newValue
                ? {
                    enabled: true,
                    amount: 1,
                  }
                : {
                    enabled: false,
                  };
              setFieldValue("noShowFee", value);
            }}
          />
          <FormikInput
            className={cn("transition-opacity w-16", {
              "opacity-0": !values.noShowFee.enabled,
            })}
            name="noShowFee.amount"
            formikProps={formik}
            {...onlyNumbersProps}
          />
        </RulesComponents.CardLabel>
      </RulesComponents.FormRow>

      {!values.isDefault && (
        <RulesComponents.FormRow
          label={t("page.rules.appointmentForm.applyToAppointmentsLabel")}
        >
          <div className="flex w-full flex-col gap-2">
            <div>
              {t("page.rules.appointmentForm.applyToAppointmentsExplanation")}
            </div>

            <PaginatedSelect
              menuPlacement="top"
              value={values.appointableList}
              isMulti
              placeholder="Select..."
              onChange={(newValue) => {
                setFieldValue("appointableList", newValue);
              }}
              loadOptions={async (_, __, additional) => {
                const data = await appointableListQueryFn({
                  api,
                  filter: {
                    page: additional.page,
                    limit: 10,
                    extraParams: { status: "ACTIVE" },
                  },
                });

                return {
                  options: data.content.map((appointable) => ({
                    label: (
                      <div>
                        {appointable.name}
                        <span className="text-gray-500">
                          ({appointable.duration} minutes)
                        </span>
                      </div>
                    ),
                    value: appointable.id,
                  })),
                  hasMore: !data.last,
                  additional: {
                    page: additional.page + 1,
                  },
                };
              }}
              badgeClearIndicator
            />
          </div>
        </RulesComponents.FormRow>
      )}
    </>
  );
});
